/************************************************************************************

* [SECTION]: COLLECTION FILTER

*************************************************************************************/
if (!customElements.get('l-collection-filters')) {
	class LTACollectionFilters extends HTMLElement {
		constructor() {
			super()

			this.facetFormEl = null
			this.footerControlsEl = null
			this.sectionId = null
			this.hasSortBy = false
			this.data = {
				searchParamsInitial: '',
				searchParamsPrev: '',
				filterData: [],
			}
			this.abortController = null
			this.filterDrawer = null

			this._onFormSubmit = this._onFormSubmit.bind(this)
			this._onFormInput = l_debounce(this._onFormInput.bind(this), 800)
			this._onTriggeredFilter = this._onTriggeredFilter.bind(this)
			this._onPopState = this._onPopState.bind(this)
		}

		connectedCallback() {
			this.facetFormEl = this.querySelector('form')
			this.footerControlsEl = document.getElementById(
				`CollectionFiltersControls-${this.dataset.type}-${this.dataset.deviceVersion}`
			)

			this.sectionId = this.dataset.sectionId
			this.hasSortBy = !!this.facetFormEl.querySelector('select[name="sort_by"]')
			const searchSlice = window.location.search.slice(1)
			this.data = {
				searchParamsInitial: searchSlice,
				searchParamsPrev: searchSlice,
				filterData: [],
			}
			this.abortController = new AbortController()

			this._setupEvents()
		}

		disconnectedCallback() {
			this._removeEvents()
		}

		_setupEvents() {
			// Form submit (footer controls)
			if (this.footerControlsEl && this.facetFormEl) {
				this.facetFormEl.addEventListener('submit', this._onFormSubmit)
			}

			// Input event
			if (this.facetFormEl) {
				this.facetFormEl.addEventListener('input', this._onFormInput)
			}

			// Listen for triggered filter
			document.addEventListener('l:filters:triggeredFilter', this._onTriggeredFilter)

			// History popstate
			window.addEventListener('popstate', this._onPopState)

			// Filter-count updates
			this._updateFilterCountEventDelegation()
		}

		_removeEvents() {
			if (this.facetFormEl) {
				this.facetFormEl.removeEventListener('submit', this._onFormSubmit)
				this.facetFormEl.removeEventListener('input', this._onFormInput)
			}
			document.removeEventListener('l:filters:triggeredFilter', this._onTriggeredFilter)
			window.removeEventListener('popstate', this._onPopState)
		}

		_onFormSubmit(event) {
			event.preventDefault()

			const newSearchParams = this._createSearchParams(this.facetFormEl)
			this._handleFiltersChange(newSearchParams)
		}

		_onFormInput(event) {
			event.preventDefault()

			const newSearchParams = this._createSearchParams(this.facetFormEl)

			this.dispatchEvent(
				new CustomEvent('l:filters:input', {
					bubbles: true,
					detail: { searchParamsPrev: this.data.searchParamsPrev, newSearchParams },
				})
			)

			if (!this.footerControlsEl) this._handleFiltersChange(newSearchParams)
		}

		_onTriggeredFilter(event) {
			const newSearchParams = event.detail?.newSearchParams
			if (typeof newSearchParams === 'string') {
				this._handleFiltersChange(newSearchParams)
			}
		}

		_onPopState(event) {
			if (event.state?.collectionQuickLink) return

			const searchParams = event.state ? event.state.searchParams : this.data.searchParamsInitial
			if (searchParams === this.data.searchParamsPrev) return
			this._handleFiltersChange(searchParams, false)
		}

		_handleFiltersChange(newSearchParams, updateURLHash = true) {
			if (this.data.searchParamsPrev === newSearchParams) return

			this.data.searchParamsPrev = newSearchParams
			this.dispatchEvent(new CustomEvent('l:filters:loading', { bubbles: true }))

			const newSearchUrl = `${window.location.pathname}?section_id=${this.sectionId}&${newSearchParams}`
			const filterDataMatch = (el) => el.url === newSearchUrl

			if (this.data.filterData.some(filterDataMatch)) {
				this._renderSectionFromCache(filterDataMatch)
			} else {
				this._renderSectionFromFetch(newSearchUrl)
			}

			if (updateURLHash) this._updateURLHash(newSearchParams)

			if (!this.filterDrawer) {
				this.filterDrawer = this.closest('l-drawer')
			}

			if (this.filterDrawer && typeof this.filterDrawer.drawer_close === 'function') {
				this.filterDrawer.drawer_close()
			}
		}

		_renderSectionFromCache(filterDataMatch) {
			const cached = this.data.filterData.find(filterDataMatch)
			if (cached && cached.html) {
				this._renderContent(cached.html)
			}
		}

		_renderSectionFromFetch(url) {
			this.abortController?.abort()
			this.abortController = new AbortController()
			fetch(url, { signal: this.abortController.signal })
				.then((resp) => resp.text())
				.then((html) => {
					this.data.filterData = [...this.data.filterData, { html, url }]
					this._renderContent(html)
				})
				.catch((e) => {
					if (e.name !== 'AbortError') {
						console.error('[LTACollectionFilters] Fetch error:', e)
					}
				})
		}

		_renderContent(html) {
			if (!html) return
			const newHTML = l_getSectionDOM(html)

			// Update l-collapsible-tab content/triggers based on ids
			this.querySelectorAll('l-collapsible-tab').forEach((element) => {
				const id = element.id
				if (!id) return
				const newElement = newHTML.querySelector(`#${id}`)
				if (!newElement) return

				const triggerEl = newElement.querySelector('[data-trigger]')
				const contentEl = newElement.querySelector('[data-content]')
				const oldTrigger = element.querySelector('[data-trigger]')
				const oldContent = element.querySelector('[data-content]')

				if (triggerEl && oldTrigger) oldTrigger.innerHTML = triggerEl.innerHTML
				if (contentEl && oldContent) oldContent.innerHTML = contentEl.innerHTML
			})

			// Update footer controls
			if (this.footerControlsEl) {
				const newFooterControlsEl = newHTML.querySelector(`#${this.footerControlsEl.id}`)
				if (newFooterControlsEl) {
					this.footerControlsEl.innerHTML = newFooterControlsEl.innerHTML
				}
			}

			// Re-init trigger/input listeners and filter count
			this._removeEvents()
			this._setupEvents()

			this.dispatchEvent(
				new CustomEvent('l:filters:rendered', {
					bubbles: true,
					detail: { html: newHTML },
				})
			)
		}

		_updateURLHash(newSearchParams) {
			const url = `${window.location.pathname}${newSearchParams ? '?' + newSearchParams : ''}`
			history.pushState({ searchParams: newSearchParams }, '', url)
		}

		_createSearchParams(form) {
			const formData = new FormData(form)
			;['filter.v.price.lte', 'filter.v.price.gte'].forEach((key) => {
				if (formData.get(key) === '') formData.delete(key)
			})
			return new URLSearchParams(formData).toString()
		}

		_updateFilterCountEventDelegation() {
			this.querySelectorAll('l-collapsible-tab').forEach((tabEl) => {
				tabEl.addEventListener('input', () => {
					const selectedSortbyEl = tabEl.querySelector('input:checked')
					const sortbyEl = tabEl.querySelector('[data-selected-sort-by]')
					if (sortbyEl && selectedSortbyEl) {
						sortbyEl.textContent = selectedSortbyEl.dataset.optionName
					}
					const count = tabEl.querySelectorAll('input:checked').length
					const countEl = tabEl.querySelector('[data-filter-count]')
					if (countEl) {
						countEl.textContent = `+${count}`
						countEl.classList.toggle('l-hidden', count === 0)
					}
				})
			})
		}
	}

	customElements.define('l-collection-filters', LTACollectionFilters)
}

/************************************************************************************

* [COMPONENTS]: FILTER COMPONENTS

*************************************************************************************/

if (!customElements.get('l-collapsible-tab')) {
	class LTACollapsibleTab extends HTMLElement {
		constructor() {
			super()

			this.onTriggerClick = this.onTriggerClick.bind(this)
			this.onTransitionEnd = this.onTransitionEnd.bind(this)
		}

		connectedCallback() {
			this.triggerEl = this.querySelector('[data-trigger]')
			this.contentEl = this.querySelector('[data-content]')

			if (!this.triggerEl || !this.contentEl) return

			if (this.contentEl.id) this.triggerEl.setAttribute('aria-controls', this.contentEl.id)
			this.triggerEl.setAttribute('aria-expanded', String(this.classList.contains('is-expanded')))

			this.triggerEl.addEventListener('click', this.onTriggerClick)
			this.contentEl.addEventListener('transitionend', this.onTransitionEnd)
		}

		disconnectedCallback() {
			this.triggerEl?.removeEventListener('click', this.onTriggerClick)
			this.contentEl?.removeEventListener('transitionend', this.onTransitionEnd)
		}

		onTriggerClick() {
			this.toggle(!this.classList.contains('is-expanded'))
		}

		onTransitionEnd(event) {
			if (event.target !== this.contentEl || event.propertyName !== 'height') return

			if (this.classList.contains('is-expanded')) {
				this.contentEl.style.height = 'auto'
			} else {
				this.contentEl.hidden = true
				this.contentEl.style.removeProperty('height')
			}
		}

		toggle(shouldExpand) {
			this.classList.toggle('is-expanded', shouldExpand)
			this.triggerEl.setAttribute('aria-expanded', String(shouldExpand))

			if (shouldExpand) {
				this.contentEl.hidden = false
				this.contentEl.style.height = '0px'

				requestAnimationFrame(() => {
					this.contentEl.style.height = `${this.contentEl.scrollHeight}px`
				})
			} else {
				this.contentEl.style.height = `${this.contentEl.scrollHeight}px`

				requestAnimationFrame(() => {
					this.contentEl.style.height = '0px'
				})
			}
		}
	}

	customElements.define('l-collapsible-tab', LTACollapsibleTab)
}

if (!customElements.get('l-price-range')) {
	class LTAPriceRange extends HTMLElement {
		constructor() {
			super()

			this.rangeMin = null
			this.rangeMax = null
			this.inputMin = null
			this.inputMax = null
			this._handleInputFocus = this._handleInputFocus.bind(this)
			this._handleInputChange = this._handleInputChange.bind(this)
			this._handleRangeChange = this._handleRangeChange.bind(this)
			this._handleRangeInput = this._handleRangeInput.bind(this)
		}

		connectedCallback() {
			this.rangeMin = this.querySelector('input[type="range"]:first-child')
			this.rangeMax = this.querySelector('input[type="range"]:last-child')
			this.inputMin = this.querySelector('input[name="filter.v.price.gte"]')
			this.inputMax = this.querySelector('input[name="filter.v.price.lte"]')
			this._bindEvents()
		}

		_bindEvents() {
			if (!this.inputMin || !this.inputMax || !this.rangeMin || !this.rangeMax) return

			this.inputMin.addEventListener('focus', this._handleInputFocus)
			this.inputMax.addEventListener('focus', this._handleInputFocus)

			this.inputMin.addEventListener('change', (e) => this._handleInputChange(e, 'min'))
			this.inputMax.addEventListener('change', (e) => this._handleInputChange(e, 'max'))

			this.rangeMin.addEventListener('change', (e) => this._handleRangeChange(e, 'min'))
			this.rangeMax.addEventListener('change', (e) => this._handleRangeChange(e, 'max'))

			this.rangeMin.addEventListener('input', (e) => this._handleRangeInput(e, 'min'))
			this.rangeMax.addEventListener('input', (e) => this._handleRangeInput(e, 'max'))
		}

		_handleInputFocus(event) {
			event.target.select()
		}

		_handleInputChange(event, type) {
			event.preventDefault()
			const isMin = type === 'min'
			const input = isMin ? this.inputMin : this.inputMax
			const pairInput = isMin ? this.inputMax : this.inputMin
			const range = isMin ? this.rangeMin : this.rangeMax
			const cssVar = isMin ? '--from' : '--to'
			const modifier = isMin ? -1 : 1

			let targetVal = parseInt(event.target.value, 10) || 0
			let limitVal = parseInt(pairInput.value, 10)

			if (isNaN(limitVal)) {
				limitVal = parseInt(event.target[isMin ? 'max' : 'min'], 10) || 0
			}

			let boundVal
			if (isMin) {
				boundVal = Math.max(Math.min(targetVal, limitVal + modifier * -1), parseInt(event.target.min, 10))
			} else {
				boundVal = Math.min(Math.max(targetVal, limitVal + modifier), parseInt(event.target.max, 10))
			}

			event.target.value = boundVal
			range.value = boundVal
			this._setRangeCss(range, cssVar, boundVal)
		}

		_handleRangeChange(event, type) {
			event.stopPropagation()
			const input = type === 'min' ? this.inputMin : this.inputMax
			input.value = event.target.value
			input.dispatchEvent(new Event('change', { bubbles: true }))
		}

		_handleRangeInput(event, type) {
			const isMin = type === 'min'
			const input = isMin ? this.inputMin : this.inputMax
			const pairInput = isMin ? this.inputMax : this.inputMin
			const cssVar = isMin ? '--from' : '--to'

			const pairValue = parseInt(pairInput.value, 10)
			let value = parseInt(event.target.value, 10)

			let limit
			if (isNaN(pairValue)) {
				limit = parseInt(event.target[isMin ? 'max' : 'min'], 10)
			} else {
				limit = pairValue
			}

			value = isMin ? Math.min(value, limit - 1) : Math.max(value, limit + 1)

			event.target.value = value
			this._setRangeCss(event.target, cssVar, value)
			input.value = value
		}

		_setRangeCss(rangeElement, cssVar, value) {
			const max = parseInt(rangeElement.max, 10) || 1
			rangeElement.parentElement.style.setProperty(cssVar, `${(value / max) * 100}%`)
		}
	}

	customElements.define('l-price-range', LTAPriceRange)
}

if (!customElements.get('l-collection-count')) {
	class LTACollectionCount extends HTMLElement {
		constructor() {
			super()

			this._onFiltersLoading = this._onFiltersLoading.bind(this)
			this._onFiltersRendered = this._onFiltersRendered.bind(this)
		}

		connectedCallback() {
			document.addEventListener('l:filters:loading', this._onFiltersLoading)
			document.addEventListener('l:filters:rendered', this._onFiltersRendered)
		}

		disconnectedCallback() {
			document.removeEventListener('l:filters:loading', this._onFiltersLoading)
			document.removeEventListener('l:filters:rendered', this._onFiltersRendered)
		}

		_onFiltersLoading() {
			this.classList.add('is-loading')
		}

		_onFiltersRendered(event) {
			this.classList.remove('is-loading')

			const newHTML = event.detail.html
			const newCollectionCount = newHTML.querySelector('l-collection-count')

			if (newCollectionCount) {
				this.innerHTML = newCollectionCount.innerHTML
			}
		}
	}

	customElements.define('l-collection-count', LTACollectionCount)
}

if (!customElements.get('l-filter-footer-controls')) {
	class LTAFilterFooterControls extends HTMLElement {
		constructor() {
			super()

			this._onFiltersInput = this._onFiltersInput.bind(this)
			this._onFiltersLoading = this._onFiltersLoading.bind(this)
			this._onFiltersRendered = this._onFiltersRendered.bind(this)
			this._onClick = this._onClick.bind(this)
		}

		connectedCallback() {
			this._cacheElements()

			document.addEventListener('l:filters:input', this._onFiltersInput)
			document.addEventListener('l:filters:loading', this._onFiltersLoading)
			document.addEventListener('l:filters:rendered', this._onFiltersRendered)

			this.addEventListener('click', this._onClick)
		}

		disconnectedCallback() {
			document.removeEventListener('l:filters:input', this._onFiltersInput)
			document.removeEventListener('l:filters:loading', this._onFiltersLoading)
			document.removeEventListener('l:filters:rendered', this._onFiltersRendered)

			this.removeEventListener('click', this._onClick)
		}

		_cacheElements() {
			this.resetBtnEl = this.querySelector('a')
			this.submitBtnEl = this.querySelector('button[type="submit"]')
		}

		_onFiltersInput(event) {
			if (!this.submitBtnEl) return
			const { searchParamsPrev, newSearchParams } = event.detail
			const isUnchanged = searchParamsPrev == newSearchParams
			this.submitBtnEl.classList.toggle('is-disabled', isUnchanged)
			this.submitBtnEl.disabled = isUnchanged
		}

		_onFiltersLoading() {
			if (this.submitBtnEl) {
				this.submitBtnEl.classList.add('is-loading')
			}
		}

		_onFiltersRendered(event) {
			if (this.submitBtnEl) {
				this.submitBtnEl.classList.remove('is-loading')
			}

			this._cacheElements()
		}

		_onClick(event) {
			if (this.resetBtnEl && (event.target === this.resetBtnEl || this.resetBtnEl.contains(event.target))) {
				event.preventDefault()

				this.dispatchEvent(
					new CustomEvent('l:filters:triggeredFilter', {
						bubbles: true,
						detail: { newSearchParams: '' },
					})
				)
			}
		}
	}

	customElements.define('l-filter-footer-controls', LTAFilterFooterControls)
}

if (!customElements.get('l-collection-current-filters')) {
	class LTACollectionCurrentFilters extends HTMLElement {
		constructor() {
			super()

			this._onClick = this._onClick.bind(this)
			this._onFiltersLoading = this._onFiltersLoading.bind(this)
			this._onFiltersRendered = this._onFiltersRendered.bind(this)
		}

		connectedCallback() {
			this.addEventListener('click', this._onClick)
			document.addEventListener('l:filters:loading', this._onFiltersLoading)
			document.addEventListener('l:filters:rendered', this._onFiltersRendered)
		}

		disconnectedCallback() {
			this.removeEventListener('click', this._onClick)
			document.removeEventListener('l:filters:loading', this._onFiltersLoading)
			document.removeEventListener('l:filters:rendered', this._onFiltersRendered)
		}

		_onClick(event) {
			event.preventDefault()

			const removeLinkEl = event.target.closest('a')

			if (removeLinkEl) {
				const href = removeLinkEl.getAttribute('href') || ''
				const newSearchParams = href.split('?')[1] || ''

				this.dispatchEvent(
					new CustomEvent('l:filters:triggeredFilter', {
						bubbles: true,
						detail: { newSearchParams },
					})
				)
			}
		}

		_onFiltersLoading() {
			this.classList.add('is-loading')
		}

		_onFiltersRendered(event) {
			this.classList.remove('is-loading')

			const newHTML = event.detail.html
			if (!newHTML) return
			const newCollectionFilters = newHTML.querySelector('l-collection-current-filters')
			if (newCollectionFilters) this.innerHTML = newCollectionFilters.innerHTML
		}
	}

	customElements.define('l-collection-current-filters', LTACollectionCurrentFilters)
}

/************************************************************************************

* [COMPONENT]: PRODUCT GRID

*************************************************************************************/

if (!customElements.get('l-collection-grid')) {
	class LTACollectionGrid extends HTMLElement {
		constructor() {
			super()

			this.onFilterLoading = this.onFilterLoading.bind(this)
			this.onFilterRendered = this.onFilterRendered.bind(this)
		}

		connectedCallback() {
			this.productGridEl = this.querySelector('[data-product-grid]')

			if (typeof l_setPdpPrevCollectionPage === 'function') {
				l_setPdpPrevCollectionPage(this.dataset.pdpReturnTitle || '')
			}

			document.addEventListener('l:filters:loading', this.onFilterLoading)
			document.addEventListener('l:filters:rendered', this.onFilterRendered)
		}

		disconnectedCallback() {
			document.removeEventListener('l:filters:loading', this.onFilterLoading)
			document.removeEventListener('l:filters:rendered', this.onFilterRendered)
		}

		onFilterLoading() {
			if (this.productGridEl) {
				this.productGridEl.classList.add('is-loading')
			}
		}

		onFilterRendered(event) {
			const newHTML = event.detail.html
			if (!newHTML) return

			const newProductGridEl = newHTML.querySelector('[data-product-grid]')
			if (this.productGridEl && newProductGridEl) {
				const isEmpty = newProductGridEl.classList.contains('l-is-empty')
				this.productGridEl.classList.toggle('l-is-empty', isEmpty)
				this.productGridEl.innerHTML = newProductGridEl.innerHTML
				this.productGridEl.classList.remove('is-loading')
			}

			const newPaginationEl = newHTML.querySelector('[data-pagination]')
			const paginationEl = this.querySelector('[data-pagination]')
			if (paginationEl && newPaginationEl) {
				paginationEl.innerHTML = newPaginationEl.innerHTML
			}
		}
	}

	customElements.define('l-collection-grid', LTACollectionGrid)
}

/************************************************************************************

* [COMPONENT]: LOADMORE

*************************************************************************************/

if (!customElements.get('l-collection-pagination')) {
	class LTACollectionPagination extends HTMLElement {
		constructor() {
			super()

			this.onClick = this.onClick.bind(this)
			this.currentProductGridEl = null
		}

		connectedCallback() {
			this.addEventListener('click', this.onClick)
		}

		disconnectedCallback() {
			this.removeEventListener('click', this.onClick)
		}

		onClick(event) {
			const loadMoreBtnEl = event.target.closest('[data-load-more-btn]')
			if (!loadMoreBtnEl) return

			event.preventDefault()

			loadMoreBtnEl.classList.add('is-loading')

			const nextUrl = loadMoreBtnEl.dataset.nextUrl
			const sectionId = this.dataset.sectionId
			const fetchUrl = `${nextUrl}&section_id=${sectionId}`

			fetch(fetchUrl)
				.then((response) => response.text())
				.then((html) => {
					const newHTML = l_getSectionDOM(html)

					// Update product grid
					this.currentProductGridEl = this.currentProductGridEl || document.getElementById('main-product-grid')
					const newProductGridEl = newHTML.querySelector('[data-product-grid]')
					if (this.currentProductGridEl && newProductGridEl) {
						this.currentProductGridEl.innerHTML += newProductGridEl.innerHTML
					}

					// Update pagination HTML
					const newPaginationEl = newHTML.querySelector('[data-pagination]')
					if (newPaginationEl) {
						this.innerHTML = newPaginationEl.innerHTML
					}
				})
				.catch((error) => {
					console.error(error)
				})
		}
	}

	customElements.define('l-collection-pagination', LTACollectionPagination)
}

/************************************************************************************

* [COMPONENT]: L-SEARCH

*************************************************************************************/
class LTASearchGroup extends HTMLElement {
	constructor() {
		super()

		this.cachedResults = {}
		this.handleInput = this.handleInput.bind(this)
	}

	connectedCallback() {
		this.searchInputEl = this.querySelector('[data-search-input]')
		this.searchButtonEl = this.querySelector('[data-search-button]')
		if (!this.searchInputEl) return

		this.debouncedSearch = l_debounce(this.handleInput, 400)

		this.searchInputEl.addEventListener('input', this.debouncedSearch)
	}

	handleInput() {
		const value = this.searchInputEl.value || ''
		const searchTerm = value.trim()
		const params = new URLSearchParams(window.location.search)

		params.set('q', encodeURIComponent(searchTerm))
		params.delete('page')

		this.dispatchEvent(
			new CustomEvent('l:filters:triggeredFilter', {
				bubbles: true,
				detail: { newSearchParams: params.toString() },
			})
		)
	}
}

customElements.define('l-search-group', LTASearchGroup)

/************************************************************************************

* [COMPONENT]: COLLECTION QUICK LINKS

*************************************************************************************/
const l_collectionQuickLinksCache = new Map()

if (!customElements.get('l-collection-quick-links')) {
	class LTACollectionQuickLinks extends HTMLElement {
		constructor() {
			super()

			this.abortController = null
			this.templateSuffix = ''
			this._activeRequestId = null
			this._onClick = this._onClick.bind(this)
			this._onPopState = this._onPopState.bind(this)
		}

		connectedCallback() {
			this.templateSuffix = this.dataset.templateSuffix || ''
			this._cacheCurrentPage()
			this.addEventListener('click', this._onClick)
			window.addEventListener('popstate', this._onPopState)
		}

		disconnectedCallback() {
			this.removeEventListener('click', this._onClick)
			window.removeEventListener('popstate', this._onPopState)
			this.abortController?.abort()
		}

		_onClick(event) {
			const linkEl = event.target.closest('[data-quick-link]')
			if (!linkEl || !this.contains(linkEl)) return

			const href = linkEl.getAttribute('href')
			if (!href || href === '#') return
			if (!this._isCollectionUrl(href)) return

			event.preventDefault()
			this._navigateToCollection(this._buildCollectionUrl(href).toString(), true)
		}

		_onPopState(event) {
			if (event.state && !event.state.collectionQuickLink) return
			if (!this._isCollectionUrl(window.location.href)) return

			this._navigateToCollection(this._buildCollectionUrl(window.location.href).toString(), false, false)
		}

		_isCollectionUrl(href) {
			try {
				const url = new URL(href, window.location.origin)
				if (url.origin !== window.location.origin) return false

				const segments = url.pathname.split('/').filter(Boolean)
				const collectionsIndex = segments.indexOf('collections')
				if (collectionsIndex === -1) return false
				if (!segments[collectionsIndex + 1]) return false
				if (segments.includes('products')) return false

				return true
			} catch {
				return false
			}
		}

		_buildCollectionUrl(href) {
			const url = new URL(href, window.location.origin)
			url.search = ''

			if (this.templateSuffix) {
				url.searchParams.set('view', this.templateSuffix)
			}

			return url
		}

		_getCacheKey(href) {
			return this._buildCollectionUrl(href).toString()
		}

		_cacheCurrentPage() {
			const mainContentEl = document.getElementById('MainContent')
			if (!mainContentEl) return

			const cacheKey = this._getCacheKey(window.location.href)
			l_collectionQuickLinksCache.set(cacheKey, {
				mainHTML: mainContentEl.innerHTML,
				title: document.title,
				canonical: document.querySelector('link[rel="canonical"]')?.getAttribute('href') || '',
			})
		}

		_navigateToCollection(fetchUrl, updateHistory = true, cacheCurrentPage = true) {
			if (cacheCurrentPage) this._cacheCurrentPage()

			const cacheKey = this._getCacheKey(fetchUrl)
			const cached = l_collectionQuickLinksCache.get(cacheKey)

			if (cached) {
				this._renderCachedPage(cached, cacheKey, updateHistory)
				return
			}

			const requestId = Symbol('collection-quick-link')
			this._activeRequestId = requestId
			this._showLoading()

			this.abortController?.abort()
			this.abortController = new AbortController()

			fetch(cacheKey, { signal: this.abortController.signal })
				.then((response) => {
					if (!response.ok) throw new Error(`Failed to fetch collection: ${response.status}`)
					return response.text()
				})
				.then((html) => {
					if (this._activeRequestId !== requestId) return

					const pageData = this._parsePage(html)
					l_collectionQuickLinksCache.set(cacheKey, pageData)

					if (updateHistory) {
						history.pushState({ collectionQuickLink: true }, '', cacheKey)
					}

					this._applyPage(pageData)
				})
				.catch((error) => {
					if (error.name === 'AbortError') return
					console.error('[LTACollectionQuickLinks] Fetch error:', error)
					window.location.href = cacheKey
				})
				.finally(() => {
					if (this._activeRequestId === requestId) {
						this._hideLoading()
					}
				})
		}

		_renderCachedPage(pageData, cacheKey, updateHistory) {
			const requestId = Symbol('collection-quick-link-cache')
			this._activeRequestId = requestId
			this._showLoading()

			window.setTimeout(() => {
				if (this._activeRequestId !== requestId) return

				try {
					if (updateHistory) {
						history.pushState({ collectionQuickLink: true }, '', cacheKey)
					}

					this._applyPage(pageData)
				} finally {
					this._hideLoading()
				}
			}, 400)
		}

		_showLoading() {
			document.documentElement.classList.add('is-collection-loading')

			let overlay = document.querySelector('[data-collection-loading]')
			if (overlay) return

			overlay = document.createElement('div')
			overlay.className = 'l-sPLP__loading'
			overlay.setAttribute('data-collection-loading', '')
			overlay.setAttribute('role', 'status')
			overlay.setAttribute('aria-live', 'polite')
			overlay.innerHTML = `
				<span class="l-sPLP__loading-spinner" aria-hidden="true"></span>
				<span class="l-input-sr-only">Loading</span>
			`
			document.body.appendChild(overlay)
		}

		_hideLoading() {
			document.documentElement.classList.remove('is-collection-loading')
			document.querySelector('[data-collection-loading]')?.remove()
		}

		_parsePage(html) {
			const doc = new DOMParser().parseFromString(html, 'text/html')
			const newMain = doc.getElementById('MainContent')

			if (!newMain) {
				throw new Error('MainContent not found')
			}

			return {
				mainHTML: newMain.innerHTML,
				title: doc.title,
				canonical: doc.querySelector('link[rel="canonical"]')?.getAttribute('href') || '',
			}
		}

		_applyPage(pageData) {
			const currentMain = document.getElementById('MainContent')
			if (!currentMain || !pageData?.mainHTML) {
				throw new Error('MainContent not found')
			}

			const quickLinksEl = currentMain.querySelector('l-collection-quick-links') || this
			const quickLinksOffset = quickLinksEl.getBoundingClientRect().top

			const temp = document.createElement('div')
			temp.innerHTML = pageData.mainHTML
			// External section scripts are already loaded; skip so CEs are not redefined
			temp.querySelectorAll('script').forEach((script) => script.remove())
			// Allow AOS to replay on cached markup that already animated
			temp.querySelectorAll('.aos-animate').forEach((el) => el.classList.remove('aos-animate'))

			currentMain.innerHTML = temp.innerHTML
			document.title = pageData.title || document.title

			const currentCanonical = document.querySelector('link[rel="canonical"]')
			if (currentCanonical && pageData.canonical) {
				currentCanonical.setAttribute('href', pageData.canonical)
			}

			const nextQuickLinksEl = currentMain.querySelector('l-collection-quick-links')
			if (nextQuickLinksEl) {
				const offsetDiff = nextQuickLinksEl.getBoundingClientRect().top - quickLinksOffset
				if (offsetDiff !== 0) {
					window.scrollBy(0, offsetDiff)
				}
			}

			requestAnimationFrame(() => this._reinitPage(currentMain))
		}

		_reinitPage(container) {
			// Theme editor path: shopify:section:load → initAnimations() + preventOverflow
			const sectionEl = container.querySelector('.shopify-section')
			if (sectionEl) {
				sectionEl.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }))
			} else {
				window.dispatchEvent(new Event('resize'))
			}

			// Already-complete images won't fire `load` after innerHTML insert
			container.querySelectorAll('img.is-loading').forEach((img) => {
				if (!img.complete) return
				img.classList.remove('is-loading')
				img.parentElement?.classList.remove('is-loading')
			})

			if (typeof window.okeWidgetApi?.initAllWidgets === 'function') {
				window.okeWidgetApi.initAllWidgets()
			}
		}
	}

	customElements.define('l-collection-quick-links', LTACollectionQuickLinks)
}

